import React, { useEffect, useRef, useState } from 'react';
import { Table, Tag, Select, Button, Card, Statistic, Row, Col, Modal, Descriptions, message, Popconfirm, Image, Input, Space, Tooltip, DatePicker } from 'antd';
import request from '../utils/request';
import { fmtTime, fmtMoney } from '../utils/format';
import { copyText } from '../utils/clipboard';

const { RangePicker } = DatePicker;

const statusMap: Record<string, { color: string; text: string }> = {
  PENDING_PAY: { color: 'orange', text: '待支付' },
  PENDING_CONFIRM: { color: 'geekblue', text: '待审核' },
  PAID: { color: 'green', text: '已支付' },
  SHIPPED: { color: 'purple', text: '待收货' },
  COMPLETED: { color: 'blue', text: '已完成' },
  REFUNDING: { color: 'gold', text: '售后处理中' },
  REFUNDED: { color: 'default', text: '已退款' },
  CANCELLED: { color: 'red', text: '已取消' },
};

const deliveryMap: Record<string, { color: string; text: string }> = {
  PICKUP: { color: 'cyan', text: '到店自取' },
  DELIVERY: { color: 'purple', text: '送货到家' },
};

const EXPRESS_COMPANIES = [
  '顺丰速运', '中通快递', '圆通速递', '韵达快递',
  '申通快递', '极兔速递', '京东物流', 'EMS',
];

const REFUND_METHODS = [
  { value: 'WECHAT', label: '微信转账' },
  { value: 'ALIPAY', label: '支付宝转账' },
  { value: 'CASH', label: '现金' },
  { value: 'BANK', label: '银行转账' },
  { value: 'OTHER', label: '其他方式' },
];

// 驳回原因快捷模板：选中后作为原因前缀，用户端按前缀展示对应指引（勿随意改文案）
const PAY_REJECT_TEMPLATES = ['截图看不清', '金额不符', '未收到款', '流水号不符'];

const EVENT_TEXT: Record<string, string> = {
  AFTER_SALE_APPLY: '用户提交售后申请',
  AFTER_SALE_WITHDRAW: '用户撤回售后申请',
  AFTER_SALE_APPROVE: '商家同意退款',
  AFTER_SALE_REJECT: '商家驳回售后申请',
  REFUND_DONE: '退款完成登记',
  RETURN_REQUEST: '申请退货（用户/管理员代标）',
  RETURN_MARK_CLEARED: '已处理并清除退货标记',
};

// 列表行商品列最多展示条数，超出折叠为「等 N 件」，全量放 Tooltip
const ITEM_SHOW_MAX = 2;
const itemLabel = (i: any) => `${i.productName || i.spec} x${i.quantity}`;

const Orders: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | undefined>();
  // 查询条件：keyword=订单号/收货人/手机号/快递单号（输入框暂存，回车/点搜索才提交），range=下单日期
  const [kwInput, setKwInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [range, setRange] = useState<any>(null);
  // 配送方式 / 发货方式（快递发货）/ 实付金额区间（金额输入暂存，回车/失焦才提交）
  const [deliveryType, setDeliveryType] = useState<string | undefined>();
  const [fulfillMethod, setFulfillMethod] = useState<string | undefined>();
  // 退货标记筛选：'1'=仅看已申请退货
  const [returnRequested, setReturnRequested] = useState<string | undefined>();
  const [minAmountInput, setMinAmountInput] = useState('');
  const [maxAmountInput, setMaxAmountInput] = useState('');
  const [minAmount, setMinAmount] = useState<number | null>(null);
  const [maxAmount, setMaxAmount] = useState<number | null>(null);
  const [statusCount, setStatusCount] = useState<Record<string, number>>({});
  const [detail, setDetail] = useState<any>(null);
  const [rejectTarget, setRejectTarget] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [shipTarget, setShipTarget] = useState<number | null>(null);
  const [expressCompany, setExpressCompany] = useState('');
  const [expressNo, setExpressNo] = useState('');
  // 弹窗提交锁：驳回/发货/导出在途期间按钮 loading，失败复位可重试
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [shipSubmitting, setShipSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  // 驳回原因快捷模板（选中作前缀，补充说明写文本框）
  const [rejectTemplate, setRejectTemplate] = useState('');
  // 退款留痕登记弹窗：target 为订单行（退款关单/存量售后单处理）
  const [refundTarget, setRefundTarget] = useState<any | null>(null);
  const [rfMethod, setRfMethod] = useState('WECHAT');
  const [rfAmount, setRfAmount] = useState('');
  const [rfRefNo, setRfRefNo] = useState('');
  const [rfRemark, setRfRemark] = useState('');
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  // 竞态防护：连续切换筛选时，过期响应直接丢弃，避免旧结果覆盖新结果
  const reqSeq = useRef(0);
  const fetchList = async (p = page) => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setSelectedRowKeys([]);
    try {
      const res: any = await request.get('/order', {
        params: {
          page: p,
          pageSize,
          status,
          keyword: keyword || undefined,
          startDate: range?.[0]?.format('YYYY-MM-DD'),
          endDate: range?.[1]?.format('YYYY-MM-DD'),
          deliveryType,
          fulfillMethod,
          returnRequested,
          minAmount: minAmount ?? undefined,
          maxAmount: maxAmount ?? undefined,
        },
      });
      if (seq !== reqSeq.current) return;
      setList(res.list);
      setTotal(res.total);
      setStatusCount(res.statusCount || {});
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [page, status, keyword, range, pageSize, deliveryType, fulfillMethod, returnRequested, minAmount, maxAmount]);

  const resetFilters = () => {
    setKwInput('');
    setKeyword('');
    setRange(null);
    setStatus(undefined);
    setDeliveryType(undefined);
    setFulfillMethod(undefined);
    setReturnRequested(undefined);
    setMinAmountInput('');
    setMaxAmountInput('');
    setMinAmount(null);
    setMaxAmount(null);
    setPage(1);
  };

  // 金额区间提交：空或非法输入按"不限制"处理
  const parseAmount = (v: string): number | null => {
    const t = v.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };
  const commitAmounts = () => {
    const lo = parseAmount(minAmountInput);
    const hi = parseAmount(maxAmountInput);
    if (lo === minAmount && hi === maxAmount) return;
    setPage(1);
    setMinAmount(lo);
    setMaxAmount(hi);
  };

  const updateStatus = async (id: number, next: 'COMPLETED' | 'CANCELLED') => {
    await request.put(`/order/${id}/status`, { status: next });
    message.success('操作成功');
    fetchList();
  };

  const showDetail = async (id: number) => {
    const res: any = await request.get(`/order/${id}/detail`);
    setDetail(res);
  };

  // 核对凭证：通过 -> PAID；驳回 -> 退回待支付
  const confirmPay = async (id: number, result: 'APPROVE' | 'REJECT', reason?: string) => {
    await request.put(`/order/${id}/confirm`, { result, reason });
    message.success(result === 'APPROVE' ? '已核对通过' : '已驳回');
    fetchList();
    if (detail?.id === id) showDetail(id);
  };

  // 发货（快递）
  const shipOrder = async (id: number, company: string, no: string) => {
    await request.put(`/order/${id}/ship`, { expressCompany: company, expressNo: no });
    message.success('发货成功');
    setShipTarget(null);
    fetchList();
    if (detail?.id === id) showDetail(id);
  };

  // 主动退款关单（已付款未发货），随单登记退款留痕
  const adminRefund = async (id: number, refund?: any) => {
    await request.put(`/order/${id}/admin-refund`, { refund });
    message.success('已退款关单，退款登记已留痕');
    fetchList();
    if (detail?.id === id) showDetail(id);
  };

  // 设置/清除退货标记（线下处理完退货后清除）
  const setReturnMark = async (id: number, requested: boolean) => {
    await request.put(`/order/${id}/return-mark`, { requested });
    message.success(requested ? '已标记退货申请' : '已清除退货标记');
    fetchList();
    if (detail?.id === id) showDetail(id);
  };

  // 打开退款留痕登记弹窗（默认整单退，可改为部分金额）
  const openRefundModal = (record: any) => {
    setRefundTarget(record);
    setRfMethod('WECHAT');
    setRfAmount(Number(record.payAmount).toFixed(2));
    setRfRefNo('');
    setRfRemark('');
  };

  const submitRefund = async () => {
    if (!refundTarget || refundSubmitting) return;
    const raw = rfAmount.trim();
    let amount: number | undefined;
    if (raw) {
      amount = Number(raw);
      if (!Number.isFinite(amount) || amount <= 0) {
        message.warning('退款金额不合法');
        return;
      }
      if (amount > Number(refundTarget.payAmount) + 1e-9) {
        message.warning('退款金额不能大于实付金额');
        return;
      }
    }
    const refund = {
      method: rfMethod,
      amount,
      refNo: rfRefNo.trim() || undefined,
      remark: rfRemark.trim() || undefined,
    };
    setRefundSubmitting(true);
    try {
      await adminRefund(refundTarget.id, refund);
      setRefundTarget(null);
    } finally {
      setRefundSubmitting(false);
    }
  };

  // 驳回提交（付款凭证）：在途锁按钮，失败保持弹窗可重试
  const submitReject = async () => {
    if (rejectTarget === null || rejectSubmitting) return;
    const extra = rejectReason.trim();
    const reason = rejectTemplate
      ? (extra ? `${rejectTemplate}：${extra}` : rejectTemplate)
      : extra;
    if (!reason) {
      message.warning('请选择或填写驳回原因');
      return;
    }
    setRejectSubmitting(true);
    try {
      await confirmPay(rejectTarget, 'REJECT', reason);
      setRejectTarget(null);
    } catch {
      // 失败不关弹窗，错误提示已由请求拦截器统一给出
    } finally {
      setRejectSubmitting(false);
    }
  };

  // 快递发货提交：同上，在途期间禁止重复提交
  const submitShip = async () => {
    if (shipTarget === null || shipSubmitting) return;
    if (!expressCompany || !expressCompany.trim()) {
      message.warning('请选择快递公司');
      return;
    }
    if (!expressNo || !expressNo.trim()) {
      message.warning('请填写快递单号');
      return;
    }
    setShipSubmitting(true);
    try {
      await shipOrder(shipTarget, expressCompany.trim(), expressNo.trim());
    } catch {
      // 失败保持弹窗可重试
    } finally {
      setShipSubmitting(false);
    }
  };

  // 批量核对通过（仅待审核订单，逐单复用单笔逻辑，失败不影响其他）
  const batchConfirm = async () => {
    if (selectedRowKeys.length === 0) return;
    setBatchLoading(true);
    try {
      const res: any = await request.put('/order/admin/confirm-batch', { ids: selectedRowKeys });
      const failed = (res?.results || []).filter((r: any) => !r.ok);
      if (failed.length === 0) {
        message.success(`已批量核对通过 ${res.success} 单`);
      } else {
        Modal.warning({
          title: `批量核对完成：成功 ${res.success} 单，失败 ${failed.length} 单`,
          content: (
            <div style={{ maxHeight: 240, overflow: 'auto' }}>
              {failed.map((r: any) => (
                <div key={r.id}>{r.orderNo || `订单#${r.id}`}：{r.message || '失败'}</div>
              ))}
            </div>
          ),
        });
      }
      setSelectedRowKeys([]);
      fetchList();
    } finally {
      setBatchLoading(false);
    }
  };

  // 导出当日核对台账（CSV，带 BOM 供 Excel 打开）
  const exportLedger = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res: any = await request.get('/order/admin/confirm-ledger');
      const rows: string[][] = [
        ['订单号', '下单用户', '手机号', '应付金额', '凭证付款金额', '凭证付款时间', '付款流水/尾号', '核对时间'],
      ];
      for (const it of res?.list || []) {
        rows.push([
          it.orderNo,
          it.userName || '',
          it.userPhone || '',
          // CSV 内保留纯数字（不带 ¥），Excel 才能按数值列统计
          Number(it.payAmount ?? 0).toFixed(2),
          it.proofPayAmount != null ? Number(it.proofPayAmount).toFixed(2) : '',
          it.proofPayTime ? fmtTime(it.proofPayTime, true) : '',
          it.proofRefNo || '',
          fmtTime(it.payConfirmAt, true),
        ]);
      }
      const csv =
        '\uFEFF' +
        rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `核对台账_${res?.date || ''}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      message.success(`已导出 ${res?.list?.length ?? 0} 条，合计 ${fmtMoney(res?.totalAmount ?? 0)}`);
    } catch {
      // 失败提示已由请求拦截器统一给出，按钮复位可重试
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    { title: '订单号', dataIndex: 'orderNo', width: 190 },
    { title: '用户ID', dataIndex: 'userId', width: 70 },
    {
      title: '商品', dataIndex: 'items', width: 220, ellipsis: true,
      render: (items: any[]) => {
        const arr = items || [];
        if (arr.length === 0) return '-';
        const shown = arr.slice(0, ITEM_SHOW_MAX);
        return (
          <Tooltip title={arr.map(itemLabel).join('；')}>
            <span>
              {shown.map(itemLabel).join('；')}
              {arr.length > shown.length ? ` 等${arr.length}件` : ''}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '实付金额', dataIndex: 'payAmount', width: 100,
      render: (v: string) => fmtMoney(v),
    },
    {
      title: '付款登记', dataIndex: 'proofPayAmount', width: 150,
      render: (v: any, record: any) =>
        v != null ? (
          <span style={{ fontSize: 12 }}>
            {fmtMoney(v)}
            {record.proofRefNo ? ` / ${record.proofRefNo}` : ''}
          </span>
        ) : (
          '-'
        ),
    },
    { title: '收货人', dataIndex: 'receiverName', width: 100 },
    {
      title: '配送方式', dataIndex: 'deliveryType', width: 110,
      render: (v: string) => <Tag color={deliveryMap[v]?.color}>{deliveryMap[v]?.text || '到店自取'}</Tag>,
    },
    {
      title: '状态', dataIndex: 'status', width: 130,
      render: (v: string, record: any) => (
        <>
          <Tag color={statusMap[v]?.color}>{statusMap[v]?.text}</Tag>
          {record.returnRequested && <Tag color="red" style={{ marginTop: 2 }}>申请退货</Tag>}
        </>
      ),
    },
    { title: '下单时间', dataIndex: 'createdAt', width: 160, render: (v: string) => fmtTime(v) },
    {
      title: '操作', width: 260, fixed: 'right' as const,
      render: (_: any, record: any) => (
        <>
          <Button size="small" onClick={() => showDetail(record.id)}>详情</Button>{' '}
          {record.status === 'PENDING_CONFIRM' && (
            <Button size="small" type="primary" onClick={() => showDetail(record.id)}>核对</Button>
          )}
          {record.status === 'PAID' && record.deliveryType === 'DELIVERY' && (
            <Button size="small" type="primary" ghost onClick={() => { setShipTarget(record.id); setExpressCompany(''); setExpressNo(''); }}>快递发货</Button>
          )}
          {record.status === 'PAID' && (
            <Button size="small" danger onClick={() => openRefundModal(record)}>退款关单</Button>
          )}
          {record.status === 'PAID' && record.deliveryType === 'PICKUP' && (
            <Popconfirm title="确认完成该订单？" onConfirm={() => updateStatus(record.id, 'COMPLETED')}>
              <Button size="small" type="primary">完成</Button>{' '}
            </Popconfirm>
          )}
          {record.status === 'REFUNDING' && (
            <Button size="small" type="primary" danger onClick={() => openRefundModal(record)}>退款关单</Button>
          )}
          {['PENDING_PAY', 'PENDING_CONFIRM'].includes(record.status) && (
            <Popconfirm title="确认取消该订单？库存将回补" onConfirm={() => updateStatus(record.id, 'CANCELLED')}>
              <Button size="small" danger>取消</Button>
            </Popconfirm>
          )}
        </>
      ),
    },
  ];

  // 状态统计卡片：卡片与「总订单数」按同一份 statusCount 生成（含售后中/已退款），
  // 手写枚举漏项导致的「总计 ≠ 各状态之和」不再出现；服务端新增状态也会自动多出一张卡
  const statKeys = Array.from(new Set([...Object.keys(statusMap), ...Object.keys(statusCount)]));
  const statusTotal = statKeys.reduce((sum, k) => sum + (Number(statusCount[k]) || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
        <h2 style={{ margin: 0, flexShrink: 0 }}>订单管理</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={`确认批量核对通过所选 ${selectedRowKeys.length} 单？`}
              onConfirm={batchConfirm}
            >
              <Button type="primary" loading={batchLoading}>批量核对通过（{selectedRowKeys.length}）</Button>
            </Popconfirm>
          )}
          <Button onClick={exportLedger} loading={exporting}>导出当日台账</Button>
          <Input.Search
            allowClear
            placeholder="订单号/收货人/手机号/快递单号"
            style={{ width: 240 }}
            value={kwInput}
            onChange={(e) => setKwInput(e.target.value)}
            onSearch={(v) => { setPage(1); setKeyword(v.trim()); }}
          />
          <RangePicker
            value={range}
            onChange={(v) => { setPage(1); setRange(v); }}
            placeholder={['下单起', '下单止']}
          />
          <Select allowClear placeholder="筛选状态" style={{ width: 120 }} value={status}
            onChange={(v) => { setStatus(v); setPage(1); }}
            options={[
              { label: '待支付', value: 'PENDING_PAY' },
              { label: '待审核', value: 'PENDING_CONFIRM' },
              { label: '已支付', value: 'PAID' },
              { label: '待收货', value: 'SHIPPED' },
              { label: '已完成', value: 'COMPLETED' },
              { label: '售后中', value: 'REFUNDING' },
              { label: '已退款', value: 'REFUNDED' },
              { label: '已取消', value: 'CANCELLED' },
            ]} />
          <Select allowClear placeholder="配送方式" style={{ width: 120 }} value={deliveryType}
            onChange={(v) => { setDeliveryType(v); setPage(1); }}
            options={[
              { label: '到店自取', value: 'PICKUP' },
              { label: '送货到家', value: 'DELIVERY' },
            ]} />
          <Select allowClear placeholder="发货方式" style={{ width: 120 }} value={fulfillMethod}
            onChange={(v) => { setFulfillMethod(v); setPage(1); }}
            options={[
              { label: '快递发货', value: 'EXPRESS' },
            ]} />
          <Select allowClear placeholder="退货标记" style={{ width: 120 }} value={returnRequested}
            onChange={(v) => { setReturnRequested(v); setPage(1); }}
            options={[
              { label: '已申请退货', value: '1' },
            ]} />
          <Space size={4}>
            <Input style={{ width: 96 }} placeholder="实付≥" value={minAmountInput}
              onChange={(e) => setMinAmountInput(e.target.value)}
              onPressEnter={commitAmounts} onBlur={commitAmounts} />
            <span style={{ color: '#bfbfbf' }}>—</span>
            <Input style={{ width: 96 }} placeholder="实付≤" value={maxAmountInput}
              onChange={(e) => setMaxAmountInput(e.target.value)}
              onPressEnter={commitAmounts} onBlur={commitAmounts} />
          </Space>
          {(keyword || range || status || deliveryType || fulfillMethod || returnRequested || minAmount !== null || maxAmount !== null) && (
            <Button onClick={resetFilters}>重置</Button>
          )}
        </div>
      </div>

      <Row gutter={[16, 12]} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card size="small"><Statistic title="总订单数" value={statusTotal} /></Card>
        </Col>
        {statKeys.map((k) => (
          <Col span={4} key={k}>
            <Card size="small"><Statistic title={statusMap[k]?.text || k} value={statusCount[k] || 0} /></Card>
          </Col>
        ))}
      </Row>

      <Table columns={columns} dataSource={list} rowKey="id" loading={loading} scroll={{ x: 1250 }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as number[]),
          getCheckboxProps: (record: any) => ({ disabled: record.status !== 'PENDING_CONFIRM' }),
        }}
        pagination={{
          current: page, total, pageSize,
          showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }} />

      <Modal title="订单详情" open={!!detail} footer={null} onCancel={() => setDetail(null)} width={680}>
        {detail && (
          <>
            {/* 下单用户信息 */}
            {detail.user && (
              <Descriptions column={2} size="small" bordered style={{ marginBottom: 12 }}>
                <Descriptions.Item label="下单用户">
                  {detail.user.nickname || detail.user.realName || `用户#${detail.user.id}`}
                </Descriptions.Item>
                <Descriptions.Item label="用户手机">{detail.user.phone || '-'}</Descriptions.Item>
              </Descriptions>
            )}

            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="订单号" span={2}>{detail.orderNo}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={statusMap[detail.status]?.color}>{statusMap[detail.status]?.text}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="配送方式">
                <Tag color={deliveryMap[detail.deliveryType]?.color}>
                  {deliveryMap[detail.deliveryType]?.text || '到店自取'}
                </Tag>
                {detail.fulfillMethod && (
                  <Tag color="blue" style={{ marginLeft: 4 }}>
                    快递发货
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="总金额">{fmtMoney(detail.totalAmount)}</Descriptions.Item>
              <Descriptions.Item label="实付金额">{fmtMoney(detail.payAmount)}</Descriptions.Item>
              {detail.deliveryType === 'PICKUP' && (
                <Descriptions.Item label="自取门店" span={2}>{detail.store?.name || '未指定'}</Descriptions.Item>
              )}
              <Descriptions.Item label={detail.deliveryType === 'PICKUP' ? '联系人' : '收货人'}>
                {detail.receiverName || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="手机号">{detail.receiverPhone || '-'}</Descriptions.Item>
              {detail.deliveryType === 'DELIVERY' && detail.receiverAddress && (
                <Descriptions.Item label="收货地址" span={2}>
                  <span>{detail.receiverAddress}</span>
                  <a
                    href={`https://apis.map.qq.com/uri/v1/geocoder?address=${encodeURIComponent(detail.receiverAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginLeft: 8, fontSize: 12 }}
                  >
                    地图查看
                  </a>
                  <Button
                    type="link"
                    size="small"
                    style={{ fontSize: 12, padding: '0 4px' }}
                    onClick={() => copyText(detail.receiverAddress, '地址已复制')}
                  >
                    复制
                  </Button>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="备注" span={2}>{detail.remark || '-'}</Descriptions.Item>
              <Descriptions.Item label="下单时间" span={2}>
                {fmtTime(detail.createdAt, true)}
              </Descriptions.Item>
              {detail.payConfirmAt && (
                <Descriptions.Item label="收款确认时间" span={2}>
                  {fmtTime(detail.payConfirmAt, true)}
                </Descriptions.Item>
              )}
              {detail.expressCompany && (
                <Descriptions.Item label="快递公司">{detail.expressCompany}</Descriptions.Item>
              )}
              {detail.expressNo && (
                <Descriptions.Item label="快递单号">
                  <span>{detail.expressNo}</span>
                  <Button
                    type="link"
                    size="small"
                    style={{ fontSize: 12, padding: '0 4px' }}
                    onClick={() => copyText(detail.expressNo, '单号已复制')}
                  >
                    复制
                  </Button>
                </Descriptions.Item>
              )}
              {detail.shippedAt && (
                <Descriptions.Item label="发货时间" span={2}>
                  {fmtTime(detail.shippedAt, true)}
                </Descriptions.Item>
              )}
              {detail.afterSaleReason && (
                <Descriptions.Item label="售后申请" span={2}>
                  {detail.afterSaleType === 'RETURN_REFUND' ? '退货退款' : detail.afterSaleType === 'REFUND_ONLY' ? '仅退款' : ''}
                  {detail.afterSaleType ? ' · ' : ''}
                  {detail.afterSaleReason}
                  {detail.afterSaleApplyAt && (
                    <span style={{ color: '#8c8c8c', marginLeft: 8 }}>
                      （{fmtTime(detail.afterSaleApplyAt, true)}）
                    </span>
                  )}
                  {detail.afterSaleImages && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      {String(detail.afterSaleImages)
                        .split('\n')
                        .filter(Boolean)
                        .map((url: string) => (
                          <Image key={url} src={url} width={80} height={80} style={{ objectFit: 'cover', borderRadius: 4 }} />
                        ))}
                    </div>
                  )}
                </Descriptions.Item>
              )}
              {detail.afterSaleRejectReason && (
                <Descriptions.Item label="售后驳回原因" span={2}>
                  <span style={{ color: '#cf1322' }}>{detail.afterSaleRejectReason}</span>
                </Descriptions.Item>
              )}
              {detail.refundedAt && (
                <Descriptions.Item label="退款登记" span={2}>
                  {fmtTime(detail.refundedAt, true)}
                  {detail.refundAmount != null && ` · ${fmtMoney(detail.refundAmount)}`}
                  {detail.refundMethod && ` · ${REFUND_METHODS.find((m) => m.value === detail.refundMethod)?.label || detail.refundMethod}`}
                  {detail.refundRefNo && ` · 流水 ${detail.refundRefNo}`}
                  {detail.refundRemark && ` · ${detail.refundRemark}`}
                </Descriptions.Item>
              )}
            </Descriptions>

            {detail.events?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>处理记录</div>
                {detail.events.map((ev: any) => (
                  <div key={ev.id} style={{ fontSize: 12, color: '#595959', marginBottom: 4 }}>
                    <Tag>{EVENT_TEXT[ev.kind] || ev.kind}</Tag>
                    {ev.note && <span>{ev.note}</span>}
                    <span style={{ color: '#8c8c8c', marginLeft: 8 }}>{fmtTime(ev.createdAt, true)}</span>
                  </div>
                ))}
              </div>
            )}

            {(detail.payProofImage || detail.rejectReason || detail.proofPayAmount != null || detail.proofPayTime || detail.proofRefNo) && (
              <div style={{ marginBottom: 16 }}>
                {detail.payProofImage && (
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>付款凭证</div>
                    <Image src={detail.payProofImage} width={220} />
                  </div>
                )}
                {(detail.proofPayAmount != null || detail.proofPayTime || detail.proofRefNo) && (
                  <Descriptions column={3} size="small" bordered style={{ marginTop: 12 }} title="用户登记的付款信息">
                    <Descriptions.Item label="付款金额">
                      {detail.proofPayAmount != null ? fmtMoney(detail.proofPayAmount) : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="付款时间">
                      {detail.proofPayTime ? fmtTime(detail.proofPayTime, true) : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="流水/尾号">{detail.proofRefNo || '-'}</Descriptions.Item>
                  </Descriptions>
                )}
                {detail.rejectReason && (
                  <div style={{ color: '#cf1322', marginTop: 8 }}>驳回原因：{detail.rejectReason}</div>
                )}
              </div>
            )}
            <Table size="small" rowKey="id" pagination={false} dataSource={detail.items}
              columns={[
                { title: '商品', dataIndex: 'productName', render: (v: string, i: any) => v || i.spec },
                { title: '规格', dataIndex: 'spec' },
                { title: '单价', dataIndex: 'price', render: (v: string) => fmtMoney(v) },
                { title: '数量', dataIndex: 'quantity' },
              ]} />

            {detail.status === 'PENDING_CONFIRM' && (
              <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Popconfirm title="确认收款无误，核对通过？" onConfirm={() => confirmPay(detail.id, 'APPROVE')}>
                  <Button type="primary">核对通过</Button>
                </Popconfirm>
                <Button danger onClick={() => { setRejectTemplate(''); setRejectReason(''); setRejectTarget(detail.id); }}>
                  驳回
                </Button>
              </div>
            )}
            {detail.status === 'PAID' && detail.deliveryType === 'DELIVERY' && (
              <div style={{ marginTop: 16, padding: 16, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8 }}>
                <Button
                  type="primary"
                  block
                  style={{ height: 48, fontSize: 15 }}
                  onClick={() => { setShipTarget(detail.id); setExpressCompany(''); setExpressNo(''); }}
                >
                  📦 快递发货
                </Button>
                <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                  发出后待用户确认收货（快递 10 天超时自动确认）
                </div>
              </div>
            )}
            {detail.status === 'PAID' && (
              <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button danger onClick={() => openRefundModal(detail)}>退款关单</Button>
              </div>
            )}
            {detail.status === 'REFUNDING' && (
              <div style={{ marginTop: 16, padding: 16, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8 }}>
                <div style={{ fontWeight: 600, marginBottom: 12, color: '#d48806' }}>
                  ⚠️ 存量线上售后单（旧流程）：
                  {detail.afterSaleType === 'RETURN_REFUND' ? '退货退款' : detail.afterSaleType === 'REFUND_ONLY' ? '仅退款' : ''}
                  {detail.afterSaleReason ? ` ${detail.afterSaleReason}` : ''}
                  {detail.afterSaleImages && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      {String(detail.afterSaleImages)
                        .split('\n')
                        .filter(Boolean)
                        .map((url: string) => (
                          <Image key={url} src={url} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 4 }} />
                        ))}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
                  售后申请入口已下线，此单仅可退款关单（线下打款后登记留痕）或继续线下协商处理
                </div>
                <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
                  <Button type="primary" danger onClick={() => openRefundModal(detail)}>退款关单</Button>
                </div>
              </div>
            )}
            {detail.returnRequested ? (
              <div style={{ marginTop: 16, padding: 16, background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 8 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, color: '#cf1322' }}>
                  🔄 用户已申请退货{detail.returnRequestAt ? `（${fmtTime(detail.returnRequestAt, true)}）` : ''}，请线下联系处理
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Popconfirm title="确认线下已处理，清除退货标记？" onConfirm={() => setReturnMark(detail.id, false)}>
                    <Button type="primary">清除退货标记</Button>
                  </Popconfirm>
                </div>
              </div>
            ) : (['PAID', 'SHIPPED', 'COMPLETED'].includes(detail.status) && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Popconfirm title="管理员代客标记退货申请？" onConfirm={() => setReturnMark(detail.id, true)}>
                  <Button danger>标记退货申请</Button>
                </Popconfirm>
              </div>
            ))}
          </>
        )}
      </Modal>

      <Modal
        title="驳回付款凭证"
        open={rejectTarget !== null}
        onCancel={() => !rejectSubmitting && setRejectTarget(null)}
        onOk={submitReject}
        okButtonProps={{ danger: true, loading: rejectSubmitting }}
      >
        {/* 快捷模板作为原因前缀，用户端按前缀展示对应指引；补充说明写下方文本框 */}
        <div style={{ marginBottom: 12 }}>
          <Space size={[8, 8]} wrap>
            {PAY_REJECT_TEMPLATES.map((t) => (
              <Button
                key={t}
                size="small"
                type={rejectTemplate === t ? 'primary' : 'default'}
                onClick={() => setRejectTemplate(rejectTemplate === t ? '' : t)}
              >
                {t}
              </Button>
            ))}
          </Space>
        </div>
        <Input.TextArea
          rows={3}
          maxLength={200}
          placeholder={rejectTemplate ? '补充说明（选填，将拼接在所选原因之后）' : '可点选上方快捷原因，或直接填写驳回原因'}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>

      {/* 退款留痕登记：打款仍线下完成，此处登记方式/金额/流水/备注供账目核对 */}
      <Modal
        title="退款关单并登记留痕"
        open={!!refundTarget}
        onCancel={() => !refundSubmitting && setRefundTarget(null)}
        onOk={submitRefund}
        okText="确认退款"
        okButtonProps={{ danger: true, loading: refundSubmitting }}
      >
        {refundTarget && (
          <div>
            <div style={{ marginBottom: 12, color: '#8c8c8c', fontSize: 12 }}>
              订单 {refundTarget.orderNo}，实付 {fmtMoney(refundTarget.payAmount)}。
              请先完成线下转账，再填写以下登记信息（未发货订单库存将自动回补，已发放积分/佣金将追回）。
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>退款方式</div>
              <Select style={{ width: '100%' }} value={rfMethod} onChange={setRfMethod} options={REFUND_METHODS} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>退款金额（默认整单，可改为部分退款）</div>
              <Input
                type="number"
                addonBefore="¥"
                value={rfAmount}
                onChange={(e) => setRfAmount(e.target.value)}
                placeholder={Number(refundTarget.payAmount).toFixed(2)}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>退款流水号/尾号（选填）</div>
              <Input maxLength={64} value={rfRefNo} onChange={(e) => setRfRefNo(e.target.value)} placeholder="如 微信转账流水尾号" />
            </div>
            <div>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>备注（选填）</div>
              <Input.TextArea rows={2} maxLength={200} value={rfRemark} onChange={(e) => setRfRemark(e.target.value)} placeholder="如：缺货关单，已电话沟通" />
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="快递发货"
        open={shipTarget !== null}
        onCancel={() => !shipSubmitting && setShipTarget(null)}
        onOk={submitShip}
        okButtonProps={{ loading: shipSubmitting }}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>快递公司</div>
          <Select
            style={{ width: '100%' }}
            placeholder="请选择快递公司"
            value={expressCompany || undefined}
            onChange={(v) => setExpressCompany(v)}
            options={EXPRESS_COMPANIES.map((c) => ({ label: c, value: c }))}
          />
        </div>
        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>快递单号</div>
          <Input
            placeholder="请输入快递单号"
            value={expressNo}
            onChange={(e) => setExpressNo(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Orders;
